import React, { useState, useEffect, ChangeEvent } from 'react';
import { useMutation } from '@apollo/client';
import {
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
} from '@mui/material';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { FragmentType, useFragment } from '../../__generated__';
import { FileObjTypes } from '../../components/types';
import ErrorView from '../../components/common/ErrorView';
import Image from '../../components/common/Image';
import CurrencyOption from '../../components/common/CurrencyOption';
import FilePicker from '../../components/common/FilePicker';
import useImageFile from '../../components/common/hooks/FileLoader';
import useImage from '../../components/common/hooks/cloudimage';
import { getSourceCurrencies, getTenantTypes } from '../../util/options';
import { getCustomerLogoFile } from '../../util/clients';

import { UPDATE_TENANT, ADD_TENANT } from '../../graphql/tenant';
import { Tenant_RowFragment } from './PlatformClientRow';
import FormDialogContainer, {
  DialogBody,
} from '../../components/common/FormDialogContainer';

export type PlatformClientFormProps = {
  open: boolean;
  tenant?: FragmentType<typeof Tenant_RowFragment>;
  onClose: () => void;
};

const PlatformClientForm = (props: PlatformClientFormProps) => {
  const { open, onClose } = props;
  const tenant = useFragment(Tenant_RowFragment, props.tenant) || null;
  const [updateTenant, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_TENANT);
  const [addTenant, { loading: addLoading, error: addError }] =
    useMutation(ADD_TENANT);

  const [name, setName] = useState<string>(tenant?.name || '');
  const [code, setCode] = useState<string>(tenant?.code || '');
  const [sourceCurrency, setSourceCurrency] = useState<string>(
    tenant?.currency ? tenant.currency : ''
  );
  const [maxBonus, setMaxBonus] = useState<number | string>(
    tenant?.maxBonus ? tenant.maxBonus : 0
  );
  const [commission, setCommission] = useState<number | string>(
    tenant?.commission ? tenant.commission : 0
  );
  const [duplicateThreshold, setDuplicateThreshold] = useState<number | string>(
    tenant?.duplicateThreshold ? tenant.duplicateThreshold : 0
  );
  const [rateChangeThreshold1, setRateChangeThreshold1] = useState<
    number | string
  >(tenant?.rateChangeThreshold1 ? tenant.rateChangeThreshold1 : 0);
  const [rateChangeThreshold2, setRateChangeThreshold2] = useState<
    number | string
  >(tenant?.rateChangeThreshold2 ? tenant.rateChangeThreshold2 : 0);
  const [rateChangeThreshold3, setRateChangeThreshold3] = useState<
    number | string
  >(tenant?.rateChangeThreshold3 ? tenant.rateChangeThreshold3 : 0);
  const [receiptLabel, setReceiptLabel] = useState<string>(
    tenant?.receiptLabel || ''
  );
  const [receiptLineOne, setReceiptLineOne] = useState<string>(
    tenant?.receiptLineOne || 'THANK YOU'
  );
  const [receiptLineTwo, setReceiptLineTwo] = useState<string>(
    tenant?.receiptLineTwo || 'Please keep for your records.'
  );
  const [receiptFootnote, setReceiptFootnote] = useState<string>(
    tenant?.receiptFootnote || ''
  );
  const [type, setType] = useState<string>(tenant?.type ? tenant.type : '');
  const [formError, setFormError] = useState<string>();
  const [cloudImage, setCloudImage] = useState({
    imageName: tenant ? getCustomerLogoFile(tenant) : '',
  });
  const [{ imageData, imageSrc, error: imageError, setImageFiles }] =
    useImageFile();
  const [{ imageUrl }] = useImage(cloudImage);
  const [logo, setLogo] = useState<string | undefined | null>();

  useEffect(() => {
    setName(tenant?.name || '');
    setCode(tenant?.code || '');
    setType(tenant?.type || '');
    setSourceCurrency(tenant?.currency || '');
    setMaxBonus(tenant?.maxBonus || 0);
    setCommission(tenant?.commission || 0);
    setDuplicateThreshold(tenant?.duplicateThreshold || 0);
    setRateChangeThreshold1(tenant?.rateChangeThreshold1 || 0);
    setRateChangeThreshold2(tenant?.rateChangeThreshold2 || 0);
    setRateChangeThreshold3(tenant?.rateChangeThreshold3 || 0);
    setReceiptLabel(tenant?.receiptLabel || '');
    setReceiptLineOne(tenant?.receiptLineOne || '');
    setReceiptLineTwo(tenant?.receiptLineTwo || '');
    setReceiptFootnote(tenant?.receiptFootnote || '');
    setFormError('');
  }, [tenant]);

  useEffect(() => {
    if (imageUrl || imageSrc) {
      setLogo(imageSrc || imageUrl);
    } else {
      setLogo('https://via.placeholder.com/80x80?text=Select%20logo');
    }
  }, [imageUrl, imageSrc]);

  const loading = tenant ? updateLoading : addLoading;
  const error = tenant ? updateError : addError;

  const handleStringChange = (
    element: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    updateCallBack: (value: string) => void
  ) => {
    const value = element.target.value;
    updateCallBack(value);
  };

  const handleCurrencyChange = (newValue: string) => {
    setSourceCurrency(newValue);
  };

  const handleMinMaxChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    updateCallBack: (value: number | string) => void,
    min: number,
    max?: number
  ) => {
    const value = event.target.value;
    if (value) {
      if (+value === 0) {
        updateCallBack(0);
      } else if (max && +value > max) {
        updateCallBack(max);
      } else if (+value < min) {
        updateCallBack(min);
      } else {
        updateCallBack(+value);
      }
    } else {
      updateCallBack('');
    }
  };
  const changeLogo = (file: FileObjTypes[]) => {
    setImageFiles(file);
  };

  const handleClearLogo = () => {
    setImageFiles([]);
    setCloudImage({ imageName: '' });
  };

  const allowSubmit = () => {
    const result =
      duplicateThreshold &&
      +duplicateThreshold > 0 &&
      +rateChangeThreshold1 >= 0 &&
      +rateChangeThreshold2 >= 0 &&
      +rateChangeThreshold3 >= 0 &&
      name &&
      code &&
      type &&
      sourceCurrency &&
      maxBonus &&
      +maxBonus > 0 &&
      (commission === 0 || (commission && +commission >= 0)) &&
      !loading;
    return Boolean(result);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    if (!imageError) {
      if (imageData) {
        const logoData = imageData;
        if (logoData) {
          console.log(logoData);
        }
      } else {
        if (!imageUrl) {
          const deleteLogo = true;
          if (deleteLogo) {
            console.log(deleteLogo);
          }
        }
      }
    }

    if (allowSubmit()) {
      const variables = {
        name,
        code,
        type: type,
        currency: sourceCurrency,
        commission: +commission,
        duplicateThreshold: +duplicateThreshold,
        rateChangeThreshold1: +rateChangeThreshold1,
        rateChangeThreshold2: +rateChangeThreshold2,
        rateChangeThreshold3: +rateChangeThreshold3,
        maxBonus: +maxBonus,
        receiptLabel,
        receiptLineOne,
        receiptLineTwo,
        receiptFootnote,
        logo: logo || '',
      };
      if (tenant) {
        const result = await updateTenant({
          variables: {
            ...variables,
            id: tenant.id,
          },
        });
        if (result?.data?.updateTenant?.success) {
          onClose();
        }
      } else {
        const result = await addTenant({
          variables,
        });
        if (result?.data?.addTenant?.success) {
          onClose();
        }
      }
    } else {
      setFormError(
        'There was an issue with the form submission. Please contact system admin.'
      );
    }
  };

  const MAX_COMMISSION = 5;
  const MAX_DUPLICATE_THRESHOLD = 72;
  const MAX_RATECHANGE_THRESHOLD = 100;
  const isEditMode = tenant && tenant.id;
  const errorMessage = formError || error?.message;

  return (
    <>
      <FormDialogContainer>
        <DialogBody
          open={open}
          loading={loading}
          disabled={!allowSubmit()}
          title={`${isEditMode ? 'Edit' : 'Add'} Platform Client`}
          onClose={onClose}
          onSubmit={handleSubmit}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                id="code"
                name="code"
                label="Client code"
                value={code}
                onChange={(e) =>
                  handleStringChange(e, (value: string) => setCode(value))
                }
                type="text"
                fullWidth
                required
                variant="standard"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                id="name"
                name="name"
                label="Name"
                value={name}
                onChange={(e) =>
                  handleStringChange(e, (value: string) => setName(value))
                }
                type="text"
                fullWidth
                required
                variant="standard"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="type"
                select
                label="Type"
                variant="standard"
                margin="dense"
                value={type}
                onChange={(e) =>
                  handleStringChange(e, (value: string) => setType(value))
                }
                fullWidth
              >
                {getTenantTypes().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <CurrencyOption
                id={'source-currency'}
                options={getSourceCurrencies()}
                label={'Source Currency'}
                value={sourceCurrency}
                onChange={(newValue: string) => handleCurrencyChange(newValue)}
                disabled={false}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                id="max-bonus"
                name="max-bonus"
                label="Max Bonus"
                value={maxBonus}
                onChange={(e) =>
                  handleMinMaxChange(
                    e,
                    (value: number | string) => setMaxBonus(value),
                    0
                  )
                }
                type="number"
                fullWidth
                required
                variant="standard"
                InputProps={{
                  inputProps: { min: 0, max: 100000 },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                id="commission"
                name="commission"
                type="number"
                label="Comission"
                value={commission}
                onChange={(e) =>
                  handleMinMaxChange(
                    e,
                    (value: number | string) => setCommission(value),
                    0,
                    MAX_COMMISSION
                  )
                }
                fullWidth
                required
                variant="standard"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  inputProps: { min: 0, max: MAX_COMMISSION, step: 0.01 },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="duplicate-threshold"
                type="number"
                name="duplicate-threshold"
                label="Duplicate Threshold (Hrs)"
                value={duplicateThreshold}
                onChange={(e) =>
                  handleMinMaxChange(
                    e,
                    (value: number | string) => setDuplicateThreshold(value),
                    0,
                    MAX_DUPLICATE_THRESHOLD
                  )
                }
                fullWidth
                required
                variant="standard"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">Hrs</InputAdornment>
                  ),
                  inputProps: { min: 0, max: MAX_DUPLICATE_THRESHOLD, step: 1 },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="ratechange-threshold1"
                type="number"
                name="ratechange-threshold1"
                label="Admin Rate Change Threshold %"
                value={rateChangeThreshold1}
                onChange={(e) =>
                  handleMinMaxChange(
                    e,
                    (value: number | string) => setRateChangeThreshold1(value),
                    0,
                    MAX_RATECHANGE_THRESHOLD
                  )
                }
                fullWidth
                required
                variant="standard"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  inputProps: {
                    min: 0,
                    max: MAX_RATECHANGE_THRESHOLD,
                    step: 1,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="ratechange-threshold2"
                type="number"
                name="ratechange-threshold2"
                label="Agent Rate Change Threshold %"
                value={rateChangeThreshold2}
                onChange={(e) =>
                  handleMinMaxChange(
                    e,
                    (value: number | string) => setRateChangeThreshold2(value),
                    0,
                    MAX_RATECHANGE_THRESHOLD
                  )
                }
                fullWidth
                required
                variant="standard"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  inputProps: {
                    min: 0,
                    max: MAX_RATECHANGE_THRESHOLD,
                    step: 1,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="ratechange-threshold3"
                type="number"
                name="ratechange-threshold3"
                label="Satellite Rate Change Threshold %"
                value={rateChangeThreshold3}
                onChange={(e) =>
                  handleMinMaxChange(
                    e,
                    (value: number | string) => setRateChangeThreshold3(value),
                    0,
                    MAX_RATECHANGE_THRESHOLD
                  )
                }
                fullWidth
                required
                variant="standard"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  inputProps: {
                    min: 0,
                    max: MAX_RATECHANGE_THRESHOLD,
                    step: 1,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="receipt-label"
                name="receipt-label"
                type="text"
                label="Receipt Label"
                value={receiptLabel}
                onChange={(e) =>
                  handleStringChange(e, (value: string) =>
                    setReceiptLabel(value)
                  )
                }
                fullWidth
                required
                variant="standard"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="receipt-line-one"
                name="receipt-line-one"
                type="text"
                label="Receipt Line 1"
                value={receiptLineOne}
                onChange={(e) =>
                  handleStringChange(e, (value: string) =>
                    setReceiptLineOne(value)
                  )
                }
                fullWidth
                variant="standard"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="receipt-line-two"
                name="receipt-line-two"
                type="text"
                label="Receipt Line 2"
                value={receiptLineTwo}
                onChange={(e) =>
                  handleStringChange(e, (value: string) =>
                    setReceiptLineTwo(value)
                  )
                }
                fullWidth
                variant="standard"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="receipt-footnote"
                name="receipt-footnote"
                type="text"
                label="Receipt Footnote"
                value={receiptFootnote}
                onChange={(e) =>
                  handleStringChange(e, (value: string) =>
                    setReceiptFootnote(value)
                  )
                }
                fullWidth
                variant="standard"
              />
            </Grid>
            <Grid sx={{ mt: 2 }} xs={6} item alignSelf={'center'}>
              <Grid container justifyContent={'flex-end'}>
                <Grid item>
                  <Image src={logo || ''} />
                </Grid>
              </Grid>
            </Grid>
            <Grid sx={{ mt: 2 }} xs={6} item alignSelf={'center'}>
              <Grid container>
                <Grid item>
                  <FilePicker
                    id={'logo'}
                    allowMultiple={false}
                    onHandleFiles={changeLogo}
                  >
                    <IconButton aria-label="add">
                      <AddBoxIcon color="primary" />
                    </IconButton>
                  </FilePicker>
                  <IconButton aria-label="delete" onClick={handleClearLogo}>
                    <DeleteOutlineIcon color="primary" />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {errorMessage && <ErrorView message={errorMessage} />}
        </DialogBody>
      </FormDialogContainer>
    </>
  );

  // return (
  //   <PlatformClientForm
  //     open={open}
  //     tenant={tenant}
  //     error={error?.message || ''}
  //     loading={loading}
  //     tenantAction={(tenant: Partial<TenantType>) =>
  //       updateTenantFunc({
  //         name: tenant.name,
  //         type: tenant.type,
  //         currency: tenant.currency,
  //         commission: tenant.commission,
  //         duplicateThreshold: tenant.duplicateThreshold,
  //         maxBonus: tenant.maxBonus,
  //         receiptLabel: tenant.receiptLabel,
  //         receiptLineOne: tenant.receiptLineOne,
  //         receiptLineTwo: tenant.receiptLineTwo,
  //         receiptFootnote: tenant.receiptFootnote,
  //         logo: tenant.logo,
  //       })
  //     }
  //     onClose={onClose}
  //   />
  // );
};

export default PlatformClientForm;
